import { useEffect } from 'react';

/**
 * Global scroll-reveal: any element with a `data-reveal` attribute fades/slides
 * into view the first time it enters the viewport. Honours reduced-motion and
 * re-scans the DOM on route changes (via MutationObserver).
 */
export const useScrollReveal = () => {
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
      return undefined;
    }

    // Track per-effect so React StrictMode's double-invocation re-observes cleanly.
    const observed = new WeakSet();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    const observeAll = () => {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        if (observed.has(el) || el.classList.contains('is-revealed')) return;
        observed.add(el);
        observer.observe(el);
      });
    };

    observeAll();

    const mutationObserver = new MutationObserver(() => observeAll());
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
};
